import { useState } from 'react';

/**
 * Marca de Copathy.
 *
 * Dos piezas distintas, y usarlas al revés se nota:
 * · `logotipo` — la palabra completa con EXPERIENCE. Va donde hay ancho.
 * · `isotipo`  — solo la figura, en su círculo marino. Va donde no lo hay:
 *                el menú plegado, la pestaña, un avatar.
 *
 * Los archivos viven en `web/public/` y los recorta
 * `scripts/preparar-logos.mjs`. Si falta alguno se cae a un marcador — que
 * **no es la marca**, solo evita que la pantalla se vea rota:
 *
 *   copathy-logo.png        logotipo a color, para fondo claro
 *   copathy-logo-claro.png  logotipo blanco y naranja, para fondo marino
 *   copathy-isotipo.png       isotipo a color (círculo marino, figura naranja)
 *   copathy-isotipo-claro.png solo la figura naranja, sin círculo, para fondo marino
 */
type Props = {
  tamano?: 'sm' | 'md' | 'lg';
  /** `claro` para fondos oscuros. */
  tono?: 'color' | 'claro';
  variante?: 'logotipo' | 'isotipo';
};

// Alturas del lockup completo (logotipo + EXPERIENCE), proporción ~2.78.
const ALTURAS = { sm: 28, md: 44, lg: 68 } as const;
// El isotipo es cuadrado, así que se le da menos alto para que pese igual.
const ALTURAS_ISOTIPO = { sm: 34, md: 44, lg: 56 } as const;

export function Marca({ tamano = 'md', tono = 'color', variante = 'logotipo' }: Props) {
  const [falla, setFalla] = useState(false);
  const esIsotipo = variante === 'isotipo';
  const alto = esIsotipo ? ALTURAS_ISOTIPO[tamano] : ALTURAS[tamano];

  const archivo = esIsotipo
    ? tono === 'claro'
      ? '/copathy-isotipo-claro.png'
      : '/copathy-isotipo.png'
    : tono === 'claro'
      ? '/copathy-logo-claro.png'
      : '/copathy-logo.png';

  if (!falla) {
    return (
      <img
        src={archivo}
        alt="Copathy Experience"
        height={alto}
        style={{ height: alto }}
        /*
          `w-auto` y nada mas: el ancho lo pone la proporcion real del archivo.
          Sin `rounded-full` a proposito -- el isotipo YA trae su circulo, y una
          mascara redonda sobre una imagen que no es exactamente cuadrada
          (la clara mide 254x256) la convertiria en una elipse.
        */
        className="w-auto shrink-0 select-none"
        onError={() => setFalla(true)}
      />
    );
  }

  /*
    Marcador mientras no esté el archivo. La forma es la del isotipo —un
    círculo— para que al llegar el real no cambie el peso del bloque. Una letra
    sobre el color de la marca es el mismo recurso que usa Polaris cuando un
    producto no tiene isotipo propio.
  */
  if (esIsotipo) {
    return (
      <span
        style={{ width: alto, height: alto }}
        className="grid shrink-0 place-items-center rounded-full bg-marino-700 font-extrabold text-naranja-500 select-none"
        title="Falta web/public/copathy-isotipo.png"
      >
        C
      </span>
    );
  }

  const escalas = {
    sm: { titulo: 'text-2xl', sub: 'text-[0.5rem] tracking-[0.42em]' },
    md: { titulo: 'text-4xl', sub: 'text-[0.6rem] tracking-[0.52em]' },
    lg: { titulo: 'text-5xl', sub: 'text-xs tracking-[0.55em]' },
  } as const;
  const e = escalas[tamano];

  return (
    <div className="inline-flex flex-col items-start leading-none select-none">
      <div className={`${e.titulo} font-extrabold tracking-tight`}>
        <span className="text-naranja-500">CO</span>
        <span className={tono === 'claro' ? 'text-white' : 'text-marino-700'}>PATHY</span>
      </div>
      <div className={`${e.sub} mt-1.5 font-bold text-naranja-500 uppercase`}>Experience</div>
    </div>
  );
}
